import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { IoSparkles } from 'react-icons/io5';

import { twMerge } from 'tailwind-merge';
import { useWorkspace } from '@/features/workspaces/hooks';
import { BaseBadge, Card } from '@/lib';
import { useGetWorkspaceQuery, useUpdateWorkspaceAiConsentMutation } from '@/services/endpoints/workspaces';
import { SuggestedAudience } from '../types';
import { SuggestedAudienceCard } from './SuggestedAudienceCard';
import { AIAnalysisLocked } from './MarketIntelligenceReport/AIAnalysisLocked';

interface SuggestedAudiencesSectionProps {
  suggestedAudiences: SuggestedAudience[];
  onCreateAudience?: (audience: SuggestedAudience) => void;
  isCreateDisabled?: boolean;
}

export const SuggestedAudiencesSection: FC<SuggestedAudiencesSectionProps> = ({ suggestedAudiences, onCreateAudience, isCreateDisabled = false }) => {
  const { t } = useTranslation('marketIntelligence');
  const workspace = useWorkspace();
  const { data: workspaceDetails, isFetching: isWorkspaceDetailsFetching } = useGetWorkspaceQuery({
    workspaceId: workspace.id,
  });
  const [updateAiConsent, { isLoading: isEnablingConsent }] = useUpdateWorkspaceAiConsentMutation();

  // Loading, error, or otherwise unknown — not equivalent to denied consent.
  if (!workspaceDetails) {
    return null;
  }

  const isAiUsageConsentEnabled = workspaceDetails.aiUsageConsent === true;

  if (isAiUsageConsentEnabled && suggestedAudiences.length === 0) {
    return null;
  }

  const handleEnableConsent = async () => {
    try {
      await updateAiConsent({ workspaceId: workspace.id, granted: true }).unwrap();
    } catch {
      // Consent toggle failures surface via RTK; keep the empty state actionable.
    }
  };

  const gridClassName = twMerge('mt-6 grid gap-5', suggestedAudiences.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2');

  return (
    <Card className='block border-primary-border bg-[linear-gradient(rgb(247,250,255)_0%,rgb(255,255,255)_120px)] p-6 drop-shadow-none sm:p-8'>
      {isAiUsageConsentEnabled ? (
        <>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <h3 className='text-3xl font-bold tracking-tight text-primary-900'>
                <span className='text-primary-alt'>{t('report.suggestedAudiences.titlePrefix')}</span> {t('report.suggestedAudiences.titleSuffix')}
              </h3>
              <p className='mt-2 text-sm text-grey-600'>{t('report.suggestedAudiences.description')}</p>
            </div>
            <BaseBadge className='shrink-0 rounded-full flex items-center gap-2 bg-brand-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-alt'>
              <IoSparkles className='size-4 animate-pulse' />
              {t('report.suggestedAudiences.badge')}
            </BaseBadge>
          </div>

          <div className={gridClassName}>
            {suggestedAudiences?.map((audience) => (
              <SuggestedAudienceCard key={audience.id} audience={audience} onCreateAudience={onCreateAudience} isCreateDisabled={isCreateDisabled} />
            ))}
          </div>
        </>
      ) : (
        <AIAnalysisLocked
          handleEnableConsent={handleEnableConsent}
          isEnablingConsent={isEnablingConsent}
          isWorkspaceDetailsFetching={isWorkspaceDetailsFetching}
        />
      )}
    </Card>
  );
};
